import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation';
import { normaliseChoice } from './slicing';
import { BASE } from './theme';

const Row = styled.div`display: flex; flex-wrap: wrap; gap: 10px 16px; align-items: flex-end;`;
const Field = styled.label`
  display: flex; flex-direction: column; gap: 4px; font-size: 13px; color: ${BASE.muted}; min-width: 10rem;
  select { padding: 8px 10px; border-radius: 8px; border: 1px solid ${BASE.line}; background: #0f131a; color: ${BASE.text}; font: inherit; font-size: 14px; min-height: 40px; }
`;
const Hint = styled.p`margin: 0; font-size: 12px; color: ${BASE.muted};`;

export const SPREAD = '__spread__';

/**
 * The table and its slice: which table, which chart, which period for bars,
 * and per dimension either "as series" or one fixed value. Touch-sized
 * selects; at most two dimensions spread, the oldest one falls back to fixed.
 */
export default function SlicePicker({ tables, table, choice, onChange }) {
  const { t } = useTranslation();
  if (!table) return null;
  const set = (patch) => onChange(normaliseChoice(table, { ...choice, ...patch }));
  const setDim = (key, v) => {
    if (v === SPREAD) {
      const spread = [...choice.spread.filter((k) => k !== key), key];
      const dropped = spread.length > 2 ? spread.shift() : null;
      const fixed = { ...choice.fixed };
      delete fixed[key];
      if (dropped) fixed[dropped] = table.dimensions.find((d) => d.key === dropped)?.values[0];
      set({ spread, fixed });
    } else {
      set({ spread: choice.spread.filter((k) => k !== key), fixed: { ...choice.fixed, [key]: v } });
    }
  };
  return (
    <>
      <Row>
        <Field>
          {t('statisticsView.table')}
          <select value={table.id} onChange={(e) => onChange(normaliseChoice(tables.find((x) => x.id === e.target.value), null))}>
            {tables.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
          </select>
        </Field>
        <Field>
          {t('statisticsView.chart')}
          <select value={choice.chart} onChange={(e) => set({ chart: e.target.value })}>
            <option value="line">{t('statisticsView.chartLine')}</option>
            <option value="bars">{t('statisticsView.chartBars')}</option>
          </select>
        </Field>
        {choice.chart === 'bars' && (
          <Field>
            {t('statisticsView.period')}
            <select value={choice.period || ''} onChange={(e) => set({ period: e.target.value })}>
              {table.periods.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        )}
      </Row>
      {table.dimensions.length > 0 && (
        <Row>
          {table.dimensions.map((d) => (
            <Field key={d.key}>
              {d.label}
              <select value={choice.spread.includes(d.key) ? SPREAD : (choice.fixed[d.key] ?? d.values[0])} onChange={(e) => setDim(d.key, e.target.value)}>
                <option value={SPREAD}>{t('statisticsView.spread')}</option>
                {d.values.map((v) => <option key={v} value={v}>{t('statisticsView.fixed')}: {v}</option>)}
              </select>
            </Field>
          ))}
          <Hint>{t('statisticsView.spreadHint')}</Hint>
        </Row>
      )}
    </>
  );
}
